import { getTabId } from "@/utils/tabId";

export const CLOSE_CODES = {
  NORMAL: 1000,
  GOING_AWAY: 1001,
  ABNORMAL: 1006,
  POLICY_VIOLATION: 1008,
  SERVER_ERROR: 1011,
  AUTH_FAILED: 4001,
  FORBIDDEN: 4003,
  NOT_FOUND: 4004,
  RATE_LIMITED: 4008,
  TOO_MANY_CONNECTIONS: 4029,
};

const RETRYABLE_CODES = new Set([
  CLOSE_CODES.GOING_AWAY,
  CLOSE_CODES.ABNORMAL,
  CLOSE_CODES.SERVER_ERROR,
  CLOSE_CODES.RATE_LIMITED,
  CLOSE_CODES.TOO_MANY_CONNECTIONS,
]);

const NON_RETRYABLE_CODES = new Set([
  CLOSE_CODES.NORMAL,
  CLOSE_CODES.POLICY_VIOLATION,
  CLOSE_CODES.FORBIDDEN,
  CLOSE_CODES.NOT_FOUND,
]);

export function isRetryableClose(code) {
  if (RETRYABLE_CODES.has(code)) {
    return true;
  }
  if (NON_RETRYABLE_CODES.has(code)) {
    return false;
  }
  if (code === CLOSE_CODES.AUTH_FAILED) {
    return false;
  }
  return true;
}

export function getBackoffDelay(retryCount, { baseDelay = 3000, maxDelay = 30000 } = {}) {
  return Math.min(baseDelay * 2 ** retryCount, maxDelay);
}

class WebSocketManager {
  constructor() {
    this.webSockets = {};
    this._pending = {};
  }

  createWebSocket(id, defaultUrl, { onMessage, onClose, onError } = {}) {
    const existing = this.webSockets[id];

    if (existing && existing.readyState === WebSocket.OPEN) {
      return Promise.resolve(existing);
    }

    if (existing && existing.readyState === WebSocket.CONNECTING) {
      return this._pending[id] || Promise.resolve(existing);
    }

    if (this._pending[id]) {
      return this._pending[id];
    }

    const auth = JSON.parse(window.localStorage.getItem("auth")) || {};
    // ?tab= lets the backend count this tab's open WebSocket per user for the ops
    // dashboard. Same id as the X-Tab-Id request header, so the two signals match.
    const url = `${defaultUrl}/?token=${auth?.access_token}&tab=${getTabId()}`;

    const promise = new Promise((resolve, reject) => {
      const ws = new WebSocket(url);
      // Track the socket immediately, not just on open, so closeWebSocket can find and
      // close it mid-handshake. Otherwise a close during CONNECTING only clears _pending
      // and the browser socket opens later with stale callbacks still attached.
      this.webSockets[id] = ws;
      let opened = false;

      ws.onopen = () => {
        opened = true;
        delete this._pending[id];
        resolve(ws);
      };

      ws.onerror = (event) => {
        if (onError) {
          onError(event);
        }
      };

      ws.onmessage = (event) => {
        if (onMessage) {
          onMessage(event);
        }
      };

      ws.onclose = (event) => {
        // Only evict our own entries: a stale socket closing must not remove a newer
        // reconnect attempt that has since taken this id's slot.
        if (this.webSockets[id] === ws) {
          delete this.webSockets[id];
        }
        if (this._pending[id] === promise) {
          delete this._pending[id];
        }

        if (onClose) {
          onClose(event);
        }

        if (!opened) {
          reject(new Error(`WebSocket connection closed (code: ${event.code}).`));
        }
      };
    });

    this._pending[id] = promise;
    return promise;
  }

  getWebSocket(id) {
    const ws = this.webSockets[id];

    if (ws && ws.readyState === WebSocket.OPEN) {
      return ws;
    }

    return null;
  }

  send(id, data) {
    const ws = this.getWebSocket(id);

    if (!ws) {
      return false;
    }

    ws.send(typeof data === "string" ? data : JSON.stringify(data));
    return true;
  }

  closeWebSocket(id) {
    return new Promise((resolve) => {
      const ws = this.webSockets[id];

      // Once we've decided to close this id, drop the pending dedup entry up front so a
      // concurrent createWebSocket starts a fresh connection instead of awaiting this
      // dying one. The webSockets entry is removed when the socket actually closes below.
      delete this._pending[id];

      if (!ws) {
        resolve();
        return;
      }

      if (ws.readyState === WebSocket.CLOSED) {
        // Only evict our own entries: a concurrent createWebSocket may already have
        // replaced this id's slot with a newer connection.
        if (this.webSockets[id] === ws) {
          delete this.webSockets[id];
          delete this._pending[id];
        }
        resolve();
        return;
      }

      if (ws.readyState === WebSocket.CLOSING) {
        const originalOnclose = ws.onclose;
        ws.onclose = (event) => {
          if (this.webSockets[id] === ws) {
            delete this.webSockets[id];
            delete this._pending[id];
          }
          if (originalOnclose) {
            originalOnclose(event);
          }
          resolve();
        };
        return;
      }

      const originalOnclose = ws.onclose;
      ws.onclose = (event) => {
        if (this.webSockets[id] === ws) {
          delete this.webSockets[id];
          delete this._pending[id];
        }
        if (originalOnclose) {
          originalOnclose(event);
        }
        resolve();
      };

      ws.close();
    });
  }
}

export default WebSocketManager;
